"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"

type Props = {
  data: any
}

// --- Play / pause icons (black bars on the white circle, per Figma) ---

function PauseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#000" aria-hidden focusable="false">
      <rect x="3" y="1" width="4" height="14" rx="1" />
      <rect x="9" y="1" width="4" height="14" rx="1" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#000" aria-hidden focusable="false">
      <path d="M3 1.5v13l11-6.5L3 1.5z" />
    </svg>
  )
}

// --- Viewport entrance hook (fires once per page load) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    // Users who prefer reduced motion see the content immediately, no reveal.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setInView(true)
      return
    }
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      // fire once ~15% of the section has scrolled up into the viewport, so the
      // cascade plays as the block enters from the bottom rather than on mount.
      { threshold: 0, rootMargin: "0px 0px -15% 0px" },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  padding: 64px 0 0;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;
`

/* text column is centered on both breakpoints (Figma counterAxis = CENTER) */
const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 0 20px;
  margin: 0 auto 48px;
  text-align: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 32px;
    padding: 0 120px;
  }
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

/* Staggered entrance cascade — title, subtitle, body, then media reveal in
   sequence as the section scrolls into view (ASH-1833 / ASH-1914). Travel is
   40px so the motion reads clearly rather than as a barely-visible fade. */
const Reveal = styled.div<{ $in: boolean; $delay: number }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "40px")});
  transition: opacity 0.7s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $delay }) => $delay}ms;
  will-change: opacity, transform;
`

/* Media reveals last, rising while settling from a slight zoom for a clearly
   perceptible finish to the cascade. */
const MediaReveal = styled.div<{ $in: boolean; $delay: number }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "48px")})
    scale(${({ $in }) => ($in ? "1" : "1.05")});
  transition: opacity 0.7s ease, transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $delay }) => $delay}ms;
  will-change: opacity, transform;
`

/* Rich-text title: the emphasized (bold) word keeps the Figma brush underline.
   Desktop keeps the non-emphasized part lighter (500); mobile is all 700. */
const Title = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  /* Emphasized word carries the Figma brush-texture underline, identical to
     About/Opening Component 2. The spec marks a single word (title ≤16 chars),
     which never wraps, so the brush paints cleanly across it. display:inline
     (not inline-block) keeps the spaces around the accent word intact — the
     inline-block form previously swallowed them (ASH-1918). */
  strong {
    position: relative;
    display: inline;
    font-weight: 700;
    text-decoration: none;
  }
  strong::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -4px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    font-weight: 500;

    strong {
      font-weight: 700;
    }
  }
`

const Subtitle = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    line-height: 1.3;
  }
`

const BodyCopy = styled.p`
  max-width: 840px;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 0;
  /* keep the line breaks the editor typed in the textarea */
  white-space: pre-line;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    font-weight: 400;
  }
`

/* full-bleed media, 1440x488 ratio on both breakpoints (upload 2880x976) */
const MediaWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1440 / 488;
  overflow: hidden;
`

const MediaImage = styled(Image)`
  object-fit: cover;
`

const MediaVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

/* Figma 732:15156 sizes the control at 50x50 with a 22x25 glyph; it was 40x40
   with a 16x16 glyph (ASH-1919). Mobile stays at 40 — Figma's 13x13 is an
   artboard-scale artifact and far below a usable touch target. */
const PlayPauseBtn = styled.button`
  position: absolute;
  bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 50px;
    height: 50px;

    svg {
      width: 22px;
      height: 25px;
    }
  }
  border: none;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  z-index: 2;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }
`

// --- Helpers ---

function isVideoUrl(url: string): boolean {
  return url.endsWith(".mp4") || url.endsWith(".webm")
}

// --- Main Component ---

function AboutOpening1({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const { ref, inView } = useInViewOnce<HTMLDivElement>()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)

  const title = extractProperty(data, "title") as string | ""
  const subtitle = extractProperty(data, "subtitle") as string | ""
  const bodyCopy = extractProperty(data, "bodyCopy") as string | ""
  const media = extractProperty(data, "media", true) as any[] | undefined
  const mediaUrl = media?.[0]?.url || ""
  const mediaAlt = media?.[0]?.altText || ""
  const isVideo = isVideoUrl(mediaUrl)

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (isPlaying) v.pause()
    else v.play()
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.aboutOpening)
    setIsPlaying(!isPlaying)
  }

  if (!title && !subtitle && !bodyCopy && !mediaUrl) return null

  return (
    <Section ref={ref}>
      <TextBlock>
        <TitleGroup>
          {title && (
            <Reveal $in={inView} $delay={0}>
              <Title dangerouslySetInnerHTML={{ __html: title }} />
            </Reveal>
          )}
          {subtitle && (
            <Reveal $in={inView} $delay={140}>
              <Subtitle>{subtitle}</Subtitle>
            </Reveal>
          )}
        </TitleGroup>
        {bodyCopy && (
          <Reveal $in={inView} $delay={280}>
            <BodyCopy>{bodyCopy}</BodyCopy>
          </Reveal>
        )}
      </TextBlock>

      {mediaUrl && (
        <MediaReveal $in={inView} $delay={420}>
          <MediaWrapper>
            {isVideo ? (
              <>
                <MediaVideo
                  ref={videoRef}
                  src={getUmbracoMediaUrl(mediaUrl)}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
                <PlayPauseBtn
                  onClick={togglePlay}
                  style={{ left: 16 }}
                  aria-label={
                    isPlaying
                      ? isRtl
                        ? "עצור תנועת הרכיב"
                        : "Stop animation"
                      : isRtl
                      ? "הפעל תנועה רכיב"
                      : "Play animation"
                  }
                >
                  {isPlaying ? <PauseIcon /> : <PlayIcon />}
                </PlayPauseBtn>
              </>
            ) : (
              <MediaImage
                src={getUmbracoMediaUrl(mediaUrl)}
                fill
                alt={mediaAlt}
                sizes="100vw"
                priority={false}
              />
            )}
          </MediaWrapper>
        </MediaReveal>
      )}
    </Section>
  )
}

export default AboutOpening1
